/**
 * AiThinking — индикатор шагов пока AI обрабатывает запрос.
 *
 * Адаптировано из task-steps-loader (Variation A — checklist style).
 *
 * Интеграция: компонент принимает `steps` от useAiChat. Каждый tool_use_start
 * добавляет шаг со статусом `current`, tool_use_result переводит его в `done`,
 * следующий tool_use_start снова `current`. Когда стрим заканчивается — все done.
 *
 * Если шагов ещё нет — показываем дефолтный список (placeholder) с автопрогрессом
 * для случая когда backend отвечает текстом без tool-use'ов.
 */
import { useEffect, useState } from 'react';
import { Icon } from '@/shared/ui';

export type AiThinkingStep = {
  title: string;
  status: 'done' | 'current' | 'pending';
};

export interface AiThinkingProps {
  /** Реальные шаги от стрима. Если пусто — крутится placeholder. */
  steps?: AiThinkingStep[];
  /** Скорость автопрогресса placeholder'а (1 = 800ms на шаг). */
  speed?: number;
}

const PLACEHOLDER_STEPS = [
  'Читаю запрос',
  'Поднимаю данные Директа',
  'Считаю метрики',
  'Готовлю ответ',
];

export function AiThinking({ steps, speed = 1 }: AiThinkingProps) {
  const hasRealSteps = steps !== undefined && steps.length > 0;

  // Автопрогресс для placeholder режима
  const [placeholderActive, setPlaceholderActive] = useState(0);
  useEffect(() => {
    if (hasRealSteps) return;
    const id = setInterval(() => {
      setPlaceholderActive((a) => (a >= PLACEHOLDER_STEPS.length - 1 ? a : a + 1));
    }, 800 / speed);
    return () => clearInterval(id);
  }, [hasRealSteps, speed]);

  const displayList: AiThinkingStep[] =
    hasRealSteps && steps
      ? steps
      : PLACEHOLDER_STEPS.map((title, i) => ({
          title,
          status:
            i < placeholderActive ? 'done' : i === placeholderActive ? 'current' : 'pending',
        }));

  return (
    <div style={{ width: '100%' }}>
      <div className="col">
        {displayList.map((step, i) => {
          const isDone = step.status === 'done';
          const isCurrent = step.status === 'current';
          const isPending = step.status === 'pending';

          const color = isDone
            ? 'var(--text-secondary)'
            : isCurrent
              ? 'var(--text)'
              : 'var(--text-faint)';

          return (
            <div
              key={step.title}
              className="row center gap12"
              style={{
                padding: '5px 0',
                color,
                transition: 'color 300ms',
                animation: 'ai-think-row-in 350ms ease both',
                animationDelay: `${i * 0.08}s`,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isDone && (
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'var(--primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'ai-think-pop 350ms cubic-bezier(.5,1.6,.4,1) both',
                    }}
                  >
                    <Icon name="Check" size={12} style={{ color: '#fff' }} />
                  </span>
                )}
                {isCurrent && (
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: '1.8px solid var(--border)',
                      borderTopColor: 'var(--primary)',
                      borderRadius: '50%',
                      animation: `ai-think-spin ${0.9 / speed}s linear infinite`,
                    }}
                  />
                )}
                {isPending && (
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: 'var(--border-strong)',
                    }}
                  />
                )}
              </span>
              <span style={{ fontSize: 13.5, lineHeight: 1.4 }}>{step.title}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
